import { Component, Input, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import DataLabelsPlugin from 'chartjs-plugin-datalabels';
import { Chart, registerables } from 'chart.js';
import { ChartService } from 'src/app/services/charts/charts.service';
import { ChartReadyService } from 'src/app/services/charts/chart-ready.service';
import { MetadataService } from 'src/app/services/metadata/metadata.services';

Chart.register(...registerables, DataLabelsPlugin);

type IAxis = 'x' | 'y';
type ISort = 'asc' | 'desc' | 'default';
@Component({
  selector: 'app-line-chart',
  templateUrl: './line-chart.component.html',
  styleUrls: ['./line-chart.component.scss'],
})
export class LineChartComponent implements OnInit, AfterViewInit {
  @Input() title = '';
  @Input() chartId: any;
  @Input() chartCategory: any;
  @Input() wideMode = false;
  @Input() subscribed = false;
  @Input() description = '';
  @Input() axis: IAxis = 'y';
  @Input() sort: ISort = 'default';
  @Input() isCustomChart = false;
  @Input() pdfMode = false;

  public dataSets: any;
  public chart: any;
  public labels: any;
  public colors: any;

  isEmpty = false;

  @ViewChild('chartCanvas') canvasEl!: ElementRef<HTMLCanvasElement>;

  public default = true;

  constructor(
    private chartService: ChartService,
    private meta: MetadataService,
    private chartReadyService: ChartReadyService
  ) { }

  ngOnInit(): void {
    if (this.chartId) {
      this.chartReadyService.registerChart(this.chartId);
    }
  }

  ngAfterViewInit() {
    this.getChartData();
    // initChart() se llama dentro de getChartData() después de un delay
  }

  getChartData() {
    this.dataSets = this.chartService.getChartDataByCategory(
      this.chartCategory
    );

    this.isEmpty = this.dataSets.every((value: number) => value === 0);
    this.labels = this.meta.getChartLabels(this.chartCategory);
    this.colors = this.meta.getChartColors(this.chartCategory);

    // Si está vacío, marcar como listo inmediatamente (no hay nada que renderizar)
    if (this.isEmpty && this.chartId) {
      console.log(`[LineChart ${this.chartId}] Empty data, marking ready immediately`);
      this.chartReadyService.markChartReady(this.chartId);
      return;
    }

    setTimeout(() => {
      this.initChart();
    }, 300);
  }

  initChart() {
    if (!this.canvasEl) {
      console.warn('Canvas not ready yet in line chart');
      // No marcar como listo - esperar al próximo intento desde getChartData()
      return;
    }

    // Marcar como no listo mientras se inicializa
    if (this.chartId) {
      this.chartReadyService.markChartNotReady(this.chartId);
    }

    const ctx = this.canvasEl.nativeElement.getContext('2d');
    if (!ctx) {
      console.error('Cant get Canvas context');
      // Marcar como listo para evitar timeout
      if (this.chartId) {
        this.chartReadyService.markChartReady(this.chartId);
      }
      return;
    }

    // Si no es customChart, marcar como listo y salir
    if (!this.isCustomChart) {
      if (this.chartId) {
        this.chartReadyService.markChartReady(this.chartId);
      }
      return;
    }

    // Solo para isCustomChart = true
    if (this.isCustomChart) {
      let emptySet = 0;
      this.dataSets.forEach((data: any) => {
        emptySet = data === 0 ? emptySet + 1 : emptySet;
      });

      this.default = emptySet === this.dataSets.length;

      if (this.default) {
        this.dataSets = [
          0.25, 0.25, 0.25, 0.25, 0.25
        ];
        this.colors = ['#FFFFF'];
      }

      const ratio = window.devicePixelRatio || 1;
      this.canvasEl.nativeElement.width = this.canvasEl.nativeElement.offsetWidth * ratio;
      this.canvasEl.nativeElement.height = this.canvasEl.nativeElement.offsetHeight * ratio;
      ctx.scale(ratio, ratio);

      const labels = this.labels;
      const data = {
        labels: labels,
        datasets: [{
          axis: 'x',
          label: 'Int. vocacionales',
          data: this.dataSets[0],
          fill: false,
          backgroundColor: this.colors[0],
          borderColor: this.colors[0],
          tension: 0.4,
          pointRadius: 5,
          datalabels: {
            color: this.colors[0]
          }
        }, {
          axis: 'x',
          label: 'Autoeficacia',
          data: this.dataSets[1],
          fill: false,
          backgroundColor: this.colors[1],
          borderColor: this.colors[1],
          tension: 0.4,
          pointRadius: 5,
          datalabels: {
            color: this.colors[1]
          }
        }]
      } as any;

      // Configuración de animación basada en pdfMode
      const animationConfig = this.pdfMode ? false : {
        duration: 1000,
        onComplete: () => {
          if (this.chartId) {
            this.chartReadyService.markChartReady(this.chartId);
          }
        }
      };

      this.chart = new Chart(ctx, {
        type: 'line',
        data: data,
        options: {
          indexAxis: 'x',
          animation: animationConfig as any,
          scales: {
            y: {
              ticks: {
                callback: (value) => value + '%',
              }
            }
          },
          plugins: {
            datalabels: {
              display: true,
              formatter: (value) => `${Math.round(value)}%`,
              color: (ctx: any): string => String(ctx?.dataset?.borderColor ?? '#000'),
              font: {
                size: 12,
                weight: 'bold'
              },

              backgroundColor: 'rgba(255,255,255,0.85)',
              padding: 4,
              borderRadius: 4,
              borderWidth: 1,
              borderColor: (ctx: any): string => String(ctx?.dataset?.borderColor ?? 'rgba(0,0,0,0.15)'),

              align: (ctx) => {
                const datasetIndex = ctx.datasetIndex;
                const value = Number(ctx.dataset.data[ctx.dataIndex]);
                const sibling = Number(
                  ctx.chart.data.datasets[1 - datasetIndex].data[ctx.dataIndex]
                );

                if (Number.isNaN(value) || Number.isNaN(sibling)) return 'top';

                if (Math.abs(value - sibling) < 3) {
                  return datasetIndex === 0 ? 'top' : 'bottom';
                }
                return 'top';
              },

              anchor: (ctx) => {
                const datasetIndex = ctx.datasetIndex;
                const value = Number(ctx.dataset.data[ctx.dataIndex]);
                const sibling = Number(
                  ctx.chart.data.datasets[1 - datasetIndex].data[ctx.dataIndex]
                );

                if (Number.isNaN(value) || Number.isNaN(sibling)) return 'center';

                if (Math.abs(value - sibling) < 3) {
                  return datasetIndex === 0 ? 'end' : 'start';
                }
                return 'center';
              },

              offset: (ctx) => {
                const value = Number(ctx.dataset.data[ctx.dataIndex]);
                const sibling = Number(
                  ctx.chart.data.datasets[1 - ctx.datasetIndex].data[ctx.dataIndex]
                );

                const diff = Math.abs(value - sibling);

                if (diff < 2) return 14;
                if (diff < 5) return 10;

                return 6;
              },
            },

            legend: {
              display: true
            }
          }
        },
      });

      // Si modo PDF (sin animación), marcar como listo después del render
      if (this.pdfMode && this.chartId) {
        requestAnimationFrame(() => {
          this.chartReadyService.markChartReady(this.chartId);
        });
      }
    }
  }

  reInitChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
    this.getChartData();
    // Eliminada la llamada directa a initChart() - ya se llama dentro de getChartData()
  }
}
